import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Sparkles, Play, Edit, Trash2, TrendingUp, Activity, Zap, Users, Brain, CheckCircle, ChevronDown } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import {
  AGENT_STUDIO_API,
  fetchAgentStudioMcpServers,
  fetchAgentUsageStats,
  fetchCoreAgentCatalog,
  formatMcpSelectionSummary,
  formatTokenCount,
  initialMcpDraft,
  normalizeMcpServerList,
  resetCoreAgentMcpServers,
  resetCoreAgentModel,
  shortModelLabel,
  updateCoreAgentMcpServers,
  updateCoreAgentModel,
} from '@/lib/agentStudioApi';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { useWorkflowFlags } from '@/contexts/FeatureFlagContext';
import { isWorkflowCatalogAgentVisible } from '@/lib/workflowFeatureFlags';

const ICON_OPTIONS = ['cpu', 'code', 'cloud', 'link', 'zap', 'shield', 'check-circle', 'file-text', 'eye', 'layout-grid'];
const COLOR_OPTIONS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const AGENT_SUMMARIES = {
  'sap-auth': 'Reviews SAP access and security permissions',
  'nup': 'Provisions new SAP users and copies reference access',
  'ter': 'Terminates SAP users after human approval',
  'pwd_reset': 'Resets SAP user passwords and emails credentials',
  'pamter': 'PAM Health shared mailbox offboarding via Entra MCP',
  'odata-discovery': 'Finds the SAP data needed for your work',
  'abap-generator': 'Creates ABAP code from your requirements',
  'abap-validator': 'Checks ABAP code quality and standards',
  'abap-corrector': 'Fixes problems found in ABAP code',
  'abap-deploy': 'Publishes ABAP programs to your SAP system',
  'abap-atc': 'Runs ABAP Test Cockpit checks and remediates findings',
  'report-data-fetch': 'Retrieves data for reports and dashboards',
  'sales-report': 'Generates sales and business reports',
};

export default function AgentStudioEnhanced() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const workflowFlags = useWorkflowFlags();
  // Do not use agent-studio.write here — it is true for create|update|delete and
  // would unlock Save MCP / model / Delete for create-only packs.
  const canCreate = hasPermission('agent-studio', 'create');
  const canUpdate = hasPermission('agent-studio', 'update');
  const canDelete = hasPermission('agent-studio', 'delete');
  const [customAgents, setCustomAgents] = useState([]);
  const [coreAgentCatalog, setCoreAgentCatalog] = useState({ orchestrators: [], agents: [] });
  const [coreAgentStats, setCoreAgentStats] = useState([]);
  const [agentGroups, setAgentGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [selectedCoreAgent, setSelectedCoreAgent] = useState(null);
  const [modelDrafts, setModelDrafts] = useState({});
  const [modelSaving, setModelSaving] = useState({});
  const [mcpDrafts, setMcpDrafts] = useState({});
  const [mcpSaving, setMcpSaving] = useState({});
  const [usagePeriodLabel, setUsagePeriodLabel] = useState('Last 30 days');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom',
    system_prompt: '',
    model: 'gpt-4o',
    temperature: 0.7,
    max_tokens: 2000,
    icon: 'cpu',
    color: '#3b82f6'
  });

  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    agent_ids: [],
    use_master_agent: false,
    master_agent_id: null,
    orchestration_strategy: 'sequential',
    execute_parallel: false
  });

  const [coreAgentCustomization, setCoreAgentCustomization] = useState({
    custom_system_prompt: '',
    model: 'gpt-4o',
    temperature: 0.7,
    max_tokens: 4000,
    use_custom_prompt: false
  });

  useEffect(() => {
    fetchData();
  }, [workflowFlags]);

  const fetchData = async () => {
    try {
      let catalogData = null;
      let usageData = null;
      let registeredMcpServers = [];
      try {
        catalogData = await fetchCoreAgentCatalog();
      } catch (error) {
        console.error('Error loading core agent catalog:', error);
      }
      try {
        const mcpData = await fetchAgentStudioMcpServers();
        registeredMcpServers = normalizeMcpServerList(mcpData);
      } catch (error) {
        console.error('Error loading MCP server registry:', error);
        registeredMcpServers = normalizeMcpServerList(catalogData);
      }
      try {
        usageData = await fetchAgentUsageStats('last_30_days');
        setUsagePeriodLabel('Last 30 days');
      } catch {
        // usage stats optional
      }

      let customAgentsData = [];
      let groupsData = [];
      try {
        const [customRes, groupsRes] = await Promise.all([
          axios.get(`${AGENT_STUDIO_API}/agents`),
          axios.get(`${AGENT_STUDIO_API}/groups`),
        ]);
        customAgentsData = customRes.data;
        groupsData = groupsRes.data;
      } catch (error) {
        console.error('Error loading custom agents or groups:', error);
      }

      setCustomAgents(customAgentsData);
      setAgentGroups(groupsData);

      if (catalogData?.agents?.length) {
        setCoreAgentCatalog(catalogData);
        let stats = buildCoreStatsFromCatalog(catalogData, registeredMcpServers);
        if (usageData) {
          stats = mergeUsageIntoStats(stats, usageData);
        }
        setCoreAgentStats(stats);
        const drafts = {};
        const mcpDraftState = {};
        stats.forEach((agent) => {
          drafts[agent.type] = agent.selectedModel;
          mcpDraftState[agent.type] = [...(agent.selectedMcpServers || [])];
        });
        setModelDrafts(drafts);
        setMcpDrafts(mcpDraftState);
      } else {
        setCoreAgentCatalog({ orchestrators: [], agents: [] });
        setCoreAgentStats([]);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setCoreAgentStats([]);
    } finally {
      setLoading(false);
    }
  };

  const buildCoreStatsFromCatalog = (catalog, registeredMcpServers = []) => {
    const allMcpServers = registeredMcpServers.length
      ? registeredMcpServers
      : catalog.mcp_servers || [];
    return (catalog.agents || [])
      .filter((agent) => isWorkflowCatalogAgentVisible(agent, workflowFlags))
      .map((agent) => ({
      type: agent.agent_id,
      name: agent.display_name,
      totalExecutions: 0,
      completed: 0,
      failed: 0,
      inProgress: 0,
      successRate: 0,
      avgDurationSeconds: 0,
      model: shortModelLabel(agent.effective_model_id),
      effectiveModelId: agent.effective_model_id,
      configuredModelId: agent.configured_model_id,
      defaultModelId: agent.default_model_id,
      selectedModel: agent.configured_model_id || agent.effective_model_id,
      modelOptions: agent.model_options || catalog.model_options || [],
      configuredMcpServerIds: agent.configured_mcp_server_ids,
      defaultMcpServerIds: agent.default_mcp_server_ids || [],
      effectiveMcpServerIds: agent.effective_mcp_server_ids || [],
      selectedMcpServers: initialMcpDraft(
        agent.configured_mcp_server_ids,
        agent.effective_mcp_server_ids,
      ),
      mcpOptions: allMcpServers,
      description: AGENT_SUMMARIES[agent.agent_id] || agent.display_name,
      category: agent.category,
      usageTotalTokens: 0,
      usageInputTokens: 0,
      usageOutputTokens: 0,
      usageTotalRuns: 0,
    }));
  };

  const mergeUsageIntoStats = (stats, usageResponse) => {
    if (!usageResponse?.agents?.length) return stats;
    const byId = Object.fromEntries(
      usageResponse.agents.map((row) => [row.agent_id, row])
    );
    return stats.map((agent) => {
      const usage = byId[agent.type];
      if (!usage) return agent;
      const completed = usage.completed || 0;
      const failed = usage.failed || 0;
      const finished = completed + failed;
      const totalRuns = usage.total_runs || 0;
      const successRate =
        finished > 0 ? parseFloat(((completed / finished) * 100).toFixed(1)) : 0;
      return {
        ...agent,
        usageTotalTokens: usage.total_tokens || 0,
        usageInputTokens: usage.input_tokens || 0,
        usageOutputTokens: usage.output_tokens || 0,
        usageTotalRuns: totalRuns,
        // Agent telemetry v2 is the primary source for runs / success on Agent Studio.
        totalExecutions: totalRuns > 0 ? totalRuns : agent.totalExecutions,
        completed: totalRuns > 0 ? completed : agent.completed,
        failed: totalRuns > 0 ? failed : agent.failed,
        successRate: totalRuns > 0 ? successRate : agent.successRate,
      };
    });
  };

  const aggregateExecutions = (agents) =>
    agents.reduce((sum, agent) => sum + (agent.totalExecutions || agent.usageTotalRuns || 0), 0);

  const aggregateSuccessRate = (agents) => {
    const withRuns = agents.filter(
      (agent) => (agent.totalExecutions || agent.usageTotalRuns || 0) > 0
    );
    if (withRuns.length === 0) return 0;
    const weighted = withRuns.reduce(
      (sum, agent) => sum + agent.successRate * (agent.totalExecutions || agent.usageTotalRuns || 0),
      0
    );
    const totalRuns = withRuns.reduce(
      (sum, agent) => sum + (agent.totalExecutions || agent.usageTotalRuns || 0),
      0
    );
    return totalRuns > 0 ? weighted / totalRuns : 0;
  };

  const saveAgentModel = async (agentId) => {
    if (!canUpdate) return;
    const modelId = modelDrafts[agentId];
    if (!modelId) return;
    setModelSaving((prev) => ({ ...prev, [agentId]: true }));
    try {
      await updateCoreAgentModel(agentId, modelId);
      await fetchData();
    } catch (error) {
      console.error('Error saving agent model:', error);
      alert(formatAgentStudioError(error));
    } finally {
      setModelSaving((prev) => ({ ...prev, [agentId]: false }));
    }
  };

  const restoreAgentModelDefault = async (agentId) => {
    if (!canUpdate) return;
    setModelSaving((prev) => ({ ...prev, [agentId]: true }));
    try {
      await resetCoreAgentModel(agentId);
      await fetchData();
    } catch (error) {
      console.error('Error resetting agent model:', error);
      alert(formatAgentStudioError(error));
    } finally {
      setModelSaving((prev) => ({ ...prev, [agentId]: false }));
    }
  };

  const toggleMcpDraftServer = (agentId, serverId, checked) => {
    setMcpDrafts((prev) => {
      const current = new Set(prev[agentId] || []);
      if (checked) {
        current.add(serverId);
      } else {
        current.delete(serverId);
      }
      return { ...prev, [agentId]: Array.from(current) };
    });
  };

  const saveAgentMcpServers = async (agentId) => {
    if (!canUpdate) return;
    const serverIds = mcpDrafts[agentId] || [];
    if (!serverIds.length) {
      alert('Select at least one MCP server.');
      return;
    }
    setMcpSaving((prev) => ({ ...prev, [agentId]: true }));
    try {
      await updateCoreAgentMcpServers(agentId, serverIds);
      await fetchData();
    } catch (error) {
      console.error('Error saving agent MCP servers:', error);
      alert(formatAgentStudioError(error));
    } finally {
      setMcpSaving((prev) => ({ ...prev, [agentId]: false }));
    }
  };

  const restoreAgentMcpDefault = async (agentId) => {
    if (!canUpdate) return;
    setMcpSaving((prev) => ({ ...prev, [agentId]: true }));
    try {
      await resetCoreAgentMcpServers(agentId);
      await fetchData();
    } catch (error) {
      console.error('Error resetting agent MCP servers:', error);
      alert(formatAgentStudioError(error));
    } finally {
      setMcpSaving((prev) => ({ ...prev, [agentId]: false }));
    }
  };

  const mcpPickerLabel = (agent) => {
    const draft = mcpDrafts[agent.type];
    if (draft?.length) {
      return formatMcpSelectionSummary(draft, agent.mcpOptions);
    }
    return 'Select MCP servers';
  };

  const formatAgentStudioError = (error) => {
    return error?.response?.data?.detail || error?.message || 'Request failed';
  };

  const getAgentColor = (type) => {
    const colors = {
      design: 'bg-cp-inset',
      dev: 'bg-cp-cyan',
      development: 'bg-cp-cyan',
      test: 'bg-cp-faint',
      quality: 'bg-cp-faint',
      devops: 'bg-cp-imp',
      governance: 'bg-cp-ok',
      security: 'bg-cp-crit',
      discovery: 'bg-cp-indigo',
      reporting: 'bg-cp-teal',
      'sap-auth': 'bg-cp-crit',
      nup: 'bg-cp-violet',
      ter: 'bg-cp-mod',
      'odata-discovery': 'bg-cp-indigo',
    };
    return colors[type] || 'bg-cp-inset';
  };

  const coreAgentCount = coreAgentCatalog.agents?.length || coreAgentStats.length;

  const createAgent = async () => {
    try {
      await axios.post(`${AGENT_STUDIO_API}/agents`, formData);
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating agent:', error);
    }
  };

  const deleteAgent = async (agentId) => {
    if (!canDelete) return;
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    
    try {
      await axios.delete(`${AGENT_STUDIO_API}/agents/${agentId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting agent:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'custom',
      system_prompt: '',
      model: 'gpt-4o',
      temperature: 0.7,
      max_tokens: 2000,
      icon: 'cpu',
      color: '#3b82f6'
    });
  };

  const openCustomizeDialog = (agent) => {
    setSelectedCoreAgent(agent);
    setCoreAgentCustomization({
      custom_system_prompt: '',
      model: agent.model || 'gpt-4o',
      temperature: 0.7,
      max_tokens: 4000,
      use_custom_prompt: false
    });
    setCustomizeDialogOpen(true);
  };

  const saveCustomization = async () => {
    if (!selectedCoreAgent) return;
    
    try {
      await axios.post(`${INCIDENTS_API}/agents/core/${selectedCoreAgent.type}/customize`, coreAgentCustomization);
      alert(`${selectedCoreAgent.name} agent customized successfully!`);
      setCustomizeDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error customizing agent:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const createGroup = async () => {
    if (!groupFormData.name || groupFormData.agent_ids.length === 0) {
      alert('Please provide group name and select at least one agent');
      return;
    }
    
    if (groupFormData.use_master_agent && !groupFormData.master_agent_id) {
      alert('Please select a lead agent for this group');
      return;
    }
    
    try {
      await axios.post(`${AGENT_STUDIO_API}/groups`, groupFormData);
      alert('Agent group created successfully!');
      setCreateGroupDialogOpen(false);
      setGroupFormData({ 
        name: '', 
        description: '', 
        agent_ids: [], 
        use_master_agent: false,
        master_agent_id: null,
        orchestration_strategy: 'sequential',
        execute_parallel: false 
      });
      fetchData();
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Delete this agent group?')) return;
    
    try {
      await axios.delete(`${AGENT_STUDIO_API}/groups/${groupId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  const toggleAgentInGroup = (agentId) => {
    const ids = groupFormData.agent_ids.includes(agentId)
      ? groupFormData.agent_ids.filter(id => id !== agentId)
      : [...groupFormData.agent_ids, agentId];
    setGroupFormData({ ...groupFormData, agent_ids: ids });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="agent-studio">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-cp-violet" />
            Agent Studio
          </h1>
          <p className="text-muted-foreground mt-2">View and configure the agents that power your SAP workflows</p>
        </div>
        <span title={permissionDeniedTitle(canCreate, 'create agents')}>
          <Button onClick={() => setCreateDialogOpen(true)} disabled={!canCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Create Custom Agent
          </Button>
        </span>
      </div>

      {/* Analytics Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Built-in Agents</CardTitle>
            <Brain className="h-4 w-4 text-cp-violet" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{coreAgentCount}</div>
            <p className="text-xs text-muted-foreground">Ready to use out of the box</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custom Agents</CardTitle>
            <Users className="h-4 w-4 text-cp-indigo" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{customAgents.length}</div>
            <p className="text-xs text-muted-foreground">User-created agents</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Executions</CardTitle>
            <Activity className="h-4 w-4 text-cp-ok" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {aggregateExecutions(coreAgentStats).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Agent runs ({usagePeriodLabel.toLowerCase()})
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-cp-mod" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {aggregateSuccessRate(coreAgentStats).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Weighted success rate ({usagePeriodLabel.toLowerCase()})
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="core" className="w-full">
        <TabsList>
          <TabsTrigger value="core">
            <Brain className="h-4 w-4 mr-2" />
            Built-in Agents ({coreAgentCount})
          </TabsTrigger>
          <TabsTrigger value="custom">
            <Users className="h-4 w-4 mr-2" />
            Custom Agents ({customAgents.length})
          </TabsTrigger>
          <TabsTrigger value="groups">
            <Zap className="h-4 w-4 mr-2" />
            Agent Groups ({agentGroups.length})
          </TabsTrigger>
        </TabsList>

        {/* Built-in Agents Tab */}
        <TabsContent value="core" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agents</CardTitle>
              <CardDescription>
                Choose the AI model and MCP servers each agent uses. Usage is shown for{' '}
                {usagePeriodLabel.toLowerCase()}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {coreAgentStats.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  Unable to load agents. Please refresh the page or try again later.
                </div>
              ) : (
              <div className="space-y-4">
                {coreAgentStats.map((agent) => (
                  <div key={agent.type} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${getAgentColor(agent.category || agent.type)} flex items-center justify-center text-white font-bold text-sm`}>
                          {(agent.name || agent.type || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-lg">{agent.name}</div>
                          <div className="text-sm text-muted-foreground">{agent.description}</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 mb-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">AI model</Label>
                        <Select
                          value={modelDrafts[agent.type] || agent.selectedModel || ''}
                          onValueChange={(value) =>
                            setModelDrafts((prev) => ({ ...prev, [agent.type]: value }))
                          }
                          disabled={!canUpdate}
                        >
                          <SelectTrigger
                            className="mt-1"
                            title={permissionDeniedTitle(canUpdate, 'update agent models')}
                          >
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            {(agent.modelOptions || []).map((opt) => (
                              <SelectItem key={opt.model_id} value={opt.model_id}>
                                {opt.display_name || opt.model_id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Currently using {shortModelLabel(agent.effectiveModelId)}
                          {!agent.configuredModelId && ' (default)'}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <span title={permissionDeniedTitle(canUpdate, 'update agent models')}>
                            <Button
                              size="sm"
                              onClick={() => saveAgentModel(agent.type)}
                              disabled={!canUpdate || Boolean(modelSaving[agent.type])}
                            >
                              {modelSaving[agent.type] ? 'Saving…' : 'Save model'}
                            </Button>
                          </span>
                          {agent.configuredModelId && (
                            <span title={permissionDeniedTitle(canUpdate, 'reset agent models')}>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => restoreAgentModelDefault(agent.type)}
                                disabled={!canUpdate || Boolean(modelSaving[agent.type])}
                              >
                                Reset model
                              </Button>
                            </span>
                          )}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-muted-foreground">MCP servers</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="mt-1 w-full justify-between font-normal"
                              disabled={!canUpdate || !agent.mcpOptions?.length}
                              title={permissionDeniedTitle(canUpdate, 'update MCP bindings')}
                            >
                              <span className="truncate text-left">
                                {agent.mcpOptions?.length
                                  ? mcpPickerLabel(agent)
                                  : 'No MCP servers registered'}
                              </span>
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
                            {(agent.mcpOptions || []).map((server) => {
                              const checked = (mcpDrafts[agent.type] || []).includes(server.id);
                              return (
                                <label
                                  key={server.id}
                                  className="flex items-start gap-2 rounded-sm px-2 py-2 hover:bg-muted cursor-pointer"
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(value) =>
                                      toggleMcpDraftServer(agent.type, server.id, Boolean(value))
                                    }
                                  />
                                  <span className="text-sm leading-tight">
                                    <span className="font-medium">
                                      {server.display_name || server.id}
                                    </span>
                                    {server.description ? (
                                      <span className="block text-xs text-muted-foreground">
                                        {server.description}
                                      </span>
                                    ) : null}
                                  </span>
                                </label>
                              );
                            })}
                          </PopoverContent>
                        </Popover>
                        <p className="text-xs text-muted-foreground mt-1">
                          Currently using{' '}
                          {formatMcpSelectionSummary(
                            agent.configuredMcpServerIds,
                            agent.mcpOptions,
                          )}
                        </p>
                        {!agent.mcpOptions?.length && (
                          <p className="text-xs text-cp-mod mt-1">
                            Register MCP servers under Settings → MCP Servers first.
                          </p>
                        )}
                        <div className="flex gap-2 mt-2">
                          <span title={permissionDeniedTitle(canUpdate, 'update MCP bindings')}>
                            <Button
                              size="sm"
                              onClick={() => saveAgentMcpServers(agent.type)}
                              disabled={!canUpdate || Boolean(mcpSaving[agent.type]) || !agent.mcpOptions?.length}
                            >
                              {mcpSaving[agent.type] ? 'Saving…' : 'Save MCP'}
                            </Button>
                          </span>
                          {agent.configuredMcpServerIds && (
                            <span title={permissionDeniedTitle(canUpdate, 'reset MCP bindings')}>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => restoreAgentMcpDefault(agent.type)}
                                disabled={!canUpdate || Boolean(mcpSaving[agent.type])}
                              >
                                Reset MCP
                              </Button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-3 mt-1">
                      {agent.usageTotalTokens > 0 || agent.usageTotalRuns > 0 ? (
                        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4 text-sm">
                          <div>
                            <div className="text-xs text-muted-foreground">Tokens used</div>
                            <div className="font-medium">{formatTokenCount(agent.usageTotalTokens)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Runs</div>
                            <div className="font-medium">{agent.usageTotalRuns.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Success rate</div>
                            <div className="font-medium">{agent.successRate.toFixed(1)}%</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Period</div>
                            <div className="font-medium">{usagePeriodLabel}</div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">No usage recorded for {usagePeriodLabel.toLowerCase()}.</p>
                      )}
                    </div>

                    {(agent.totalExecutions > 0 || agent.usageTotalRuns > 0) && (
                      <>
                        <div className="grid grid-cols-5 gap-4 mb-3">
                          <div className="text-center p-3 bg-cp-inset rounded">
                            <div className="text-2xl font-bold text-cp-indigo">{agent.totalExecutions}</div>
                            <div className="text-xs text-muted-foreground">Total Runs</div>
                          </div>
                          <div className="text-center p-3 bg-cp-inset rounded">
                            <div className="text-2xl font-bold text-cp-ok">{agent.completed}</div>
                            <div className="text-xs text-muted-foreground">Completed</div>
                          </div>
                          <div className="text-center p-3 bg-cp-inset rounded">
                            <div className="text-2xl font-bold text-cp-crit">{agent.failed}</div>
                            <div className="text-xs text-muted-foreground">Failed</div>
                          </div>
                          <div className="text-center p-3 bg-cp-inset rounded">
                            <div className="text-2xl font-bold text-cp-mod">{agent.avgDurationSeconds}s</div>
                            <div className="text-xs text-muted-foreground">Avg Duration</div>
                          </div>
                          <div className="text-center p-3 bg-cp-inset rounded">
                            <div className="text-2xl font-bold text-cp-violet">{agent.successRate}%</div>
                            <div className="text-xs text-muted-foreground">Success Rate</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xs text-muted-foreground">Performance:</span>
                          <Progress value={agent.successRate} className="flex-1 h-2" />
                          <span className="text-xs font-medium">{agent.successRate}%</span>
                        </div>
                      </>
                    )}

                  </div>
                ))}
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Agents Tab */}
        <TabsContent value="custom" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Custom Agents</CardTitle>
              <CardDescription>
                Create specialized agents with custom prompts and configurations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {customAgents.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">No Custom Agents Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first custom agent to extend the platform's capabilities
                  </p>
                  <span title={permissionDeniedTitle(canCreate, 'create agents')}>
                    <Button onClick={() => setCreateDialogOpen(true)} disabled={!canCreate}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create First Agent
                    </Button>
                  </span>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {customAgents.map(agent => (
                    <Card key={agent.id} className="border-2">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm"
                              style={{ backgroundColor: agent.color }}
                            >
                              {agent.name.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <CardTitle className="text-base">{agent.name}</CardTitle>
                              <CardDescription className="text-xs">{agent.category}</CardDescription>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">{agent.model}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">{agent.description}</p>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Temperature: </span>
                              <span className="font-medium">{agent.temperature}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Max Tokens: </span>
                              <span className="font-medium">{agent.max_tokens}</span>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1" disabled>
                              <Play className="h-3 w-3 mr-1" />
                              Test
                            </Button>
                            <span title={permissionDeniedTitle(canDelete, 'delete agents')}>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!canDelete}
                                onClick={() => deleteAgent(agent.id)}
                              >
                                <Trash2 className="h-3 w-3 text-cp-crit" />
                              </Button>
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agent Groups Tab */}
        <TabsContent value="groups" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Agent Groups</CardTitle>
                  <CardDescription>
                    Create teams of agents to assign to tickets together
                  </CardDescription>
                </div>
                <span title={permissionDeniedTitle(canCreate, 'create agent groups')}>
                  <Button onClick={() => setCreateGroupDialogOpen(true)} disabled={!canCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Group
                  </Button>
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {agentGroups.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">No Agent Groups Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create groups to assign multiple agents to tickets at once
                  </p>
                  <span title={permissionDeniedTitle(canCreate, 'create agent groups')}>
                    <Button onClick={() => setCreateGroupDialogOpen(true)} disabled={!canCreate}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create First Group
                    </Button>
                  </span>
                </div>
              ) : (
                <div className="space-y-4">
                  {agentGroups.map(group => (
                    <Card key={group.id} className="border-2">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                              style={{ backgroundColor: group.color }}
                            >
                              <Users className="h-5 w-5" />
                            </div>
                            <div>
                              <CardTitle>{group.name}</CardTitle>
                              <CardDescription>{group.description}</CardDescription>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Badge>{group.agent_ids.length} agents</Badge>
                            <Badge variant="outline">{group.times_assigned || 0} assignments</Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {/* Master Agent Info */}
                          {group.use_master_agent && (
                            <div className="p-3 bg-cp-inset border border-cp-line rounded-lg">
                              <div className="text-xs font-semibold text-cp-fg mb-1">Lead agent</div>
                              <div className="text-xs">
                                <span className="text-muted-foreground">Coordinates: </span>
                                <Badge variant="secondary" className="text-xs">
                                  {customAgents.find(a => a.id === group.master_agent_id)?.name || 'Not set'}
                                </Badge>
                              </div>
                              <div className="text-xs mt-1">
                                <span className="text-muted-foreground">Runs: </span>
                                <span className="font-medium">
                                  {group.orchestration_strategy === 'master_controlled' ? 'Lead decides order' :
                                   group.orchestration_strategy === 'sequential' ? 'One after another' : 'All at once'}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          <div>
                            <div className="text-xs text-muted-foreground mb-2">Agents in this group:</div>
                            <div className="flex flex-wrap gap-1">
                              {group.agent_ids.map((agentId, idx) => {
                                const agent = customAgents.find(a => a.id === agentId);
                                const isMaster = group.master_agent_id === agentId;
                                return agent ? (
                                  <Badge key={idx} variant={isMaster ? "default" : "secondary"} className="text-xs">
                                    {isMaster && '👑 '}
                                    {agent.name}
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">
                              Runs {group.execute_parallel ? 'all at once' : 'one after another'}
                            </span>
                            <span title={permissionDeniedTitle(canDelete, 'delete agent groups')}>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!canDelete}
                                onClick={() => deleteGroup(group.id)}
                              >
                                <Trash2 className="h-3 w-3 text-cp-crit mr-1" />
                                Delete
                              </Button>
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Agent Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Custom Agent</DialogTitle>
            <DialogDescription>
              Design a specialized AI agent with custom capabilities
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Agent Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., SAP Fiori Expert"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="sap_expert">SAP Expert</SelectItem>
                    <SelectItem value="code_reviewer">Code Reviewer</SelectItem>
                    <SelectItem value="architect">Solution Architect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="What does this agent specialize in?"
                rows={2}
              />
            </div>

            <div>
              <Label>System Prompt *</Label>
              <Textarea
                value={formData.system_prompt}
                onChange={(e) => setFormData({...formData, system_prompt: e.target.value})}
                placeholder="You are an expert in... Your role is to..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>AI Model</Label>
                <Select value={formData.model} onValueChange={(v) => setFormData({...formData, model: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast, Cost-Effective)</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o (Balanced)</SelectItem>
                    <SelectItem value="gpt-5">GPT-5 (Most Advanced)</SelectItem>
                    <SelectItem value="gpt-5-mini">GPT-5 Mini (Fast & Advanced)</SelectItem>
                    <SelectItem value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet (Long Context)</SelectItem>
                    <SelectItem value="claude-4-sonnet-20250514">Claude 4 Sonnet (Latest)</SelectItem>
                    <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash (Fast)</SelectItem>
                    <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro (Advanced Reasoning)</SelectItem>
                    <SelectItem value="o1">OpenAI o1 (Deep Reasoning)</SelectItem>
                    <SelectItem value="o1-mini">OpenAI o1-mini (Fast Reasoning)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Choose based on complexity and budget
                </p>
              </div>
              <div>
                <Label>Max Tokens</Label>
                <Input
                  type="number"
                  value={formData.max_tokens}
                  onChange={(e) => setFormData({...formData, max_tokens: parseInt(e.target.value)})}
                  placeholder="2000"
                />
              </div>
            </div>

            <div>
              <Label>Temperature: {formData.temperature}</Label>
              <Slider
                value={[formData.temperature * 100]}
                onValueChange={(v) => setFormData({...formData, temperature: v[0] / 100})}
                max={100}
                step={1}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lower = More focused, Higher = More creative
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={createAgent}
              className="flex-1"
              disabled={!canCreate || !formData.name || !formData.system_prompt}
              title={permissionDeniedTitle(canCreate, 'create agents')}
            >
              Create Agent
            </Button>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customize Core Agent Dialog */}
      <Dialog open={customizeDialogOpen} onOpenChange={setCustomizeDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Customize {selectedCoreAgent?.name} Agent
            </DialogTitle>
            <DialogDescription>
              Override default behavior for the {selectedCoreAgent?.name} agent
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>AI Model</Label>
              <Select 
                value={coreAgentCustomization.model} 
                onValueChange={(v) => setCoreAgentCustomization({...coreAgentCustomization, model: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast & Efficient)</SelectItem>
                  <SelectItem value="gpt-4o">GPT-4o (Balanced Performance)</SelectItem>
                  <SelectItem value="gpt-5">GPT-5 (Most Advanced)</SelectItem>
                  <SelectItem value="gpt-5-mini">GPT-5 Mini (Fast & Advanced)</SelectItem>
                  <SelectItem value="o1">OpenAI o1 (Deep Reasoning)</SelectItem>
                  <SelectItem value="o1-mini">OpenAI o1-mini (Fast Reasoning)</SelectItem>
                  <SelectItem value="o1-pro">OpenAI o1-pro (Expert Reasoning)</SelectItem>
                  <SelectItem value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet (Long Context)</SelectItem>
                  <SelectItem value="claude-4-sonnet-20250514">Claude 4 Sonnet (Latest & Most Capable)</SelectItem>
                  <SelectItem value="claude-4-opus-20250514">Claude 4 Opus (Maximum Intelligence)</SelectItem>
                  <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash (Google Fast)</SelectItem>
                  <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro (Google Advanced)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Recommended: GPT-5 for design/dev, o1 for complex reasoning, Claude 4 for long context
              </p>
            </div>

            <div>
              <Label>Temperature: {coreAgentCustomization.temperature}</Label>
              <Slider
                value={[coreAgentCustomization.temperature * 100]}
                onValueChange={(v) => setCoreAgentCustomization({...coreAgentCustomization, temperature: v[0] / 100})}
                max={100}
                step={1}
              />
            </div>

            <div>
              <Label>Max Tokens</Label>
              <Input
                type="number"
                value={coreAgentCustomization.max_tokens}
                onChange={(e) => setCoreAgentCustomization({...coreAgentCustomization, max_tokens: parseInt(e.target.value)})}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="use_custom"
                checked={coreAgentCustomization.use_custom_prompt}
                onChange={(e) => setCoreAgentCustomization({...coreAgentCustomization, use_custom_prompt: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="use_custom">Use Custom System Prompt</Label>
            </div>

            {coreAgentCustomization.use_custom_prompt && (
              <div>
                <Label>Custom System Prompt</Label>
                <Textarea
                  value={coreAgentCustomization.custom_system_prompt}
                  onChange={(e) => setCoreAgentCustomization({...coreAgentCustomization, custom_system_prompt: e.target.value})}
                  placeholder="Override the default system prompt..."
                  rows={6}
                />
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={saveCustomization}
              className="flex-1"
              disabled={!canUpdate}
              title={permissionDeniedTitle(canUpdate, 'customize agents')}
            >
              Save Customization
            </Button>
            <Button variant="outline" onClick={() => setCustomizeDialogOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Agent Group Dialog */}
      <Dialog open={createGroupDialogOpen} onOpenChange={setCreateGroupDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Agent Group</DialogTitle>
            <DialogDescription>
              Create a team of agents to assign together
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Group Name *</Label>
              <Input
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({...groupFormData, name: e.target.value})}
                placeholder="e.g., SAP Fiori Team"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({...groupFormData, description: e.target.value})}
                placeholder="What does this group specialize in?"
                rows={2}
              />
            </div>

            <div>
              <Label>Select Agents ({groupFormData.agent_ids.length} selected)</Label>
              <div className="mt-2 max-h-64 overflow-y-auto border rounded p-3 space-y-2">
                {customAgents.map(agent => (
                  <div 
                    key={agent.id} 
                    className={`p-2 border rounded cursor-pointer transition ${
                      groupFormData.agent_ids.includes(agent.id) ? 'bg-cp-inset border-cp-indigo' : 'hover:bg-cp-inset'
                    }`}
                    onClick={() => toggleAgentInGroup(agent.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                          style={{ backgroundColor: agent.color }}
                        >
                          {agent.name.substring(0, 1)}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{agent.name}</div>
                          <div className="text-xs text-muted-foreground">{agent.category}</div>
                        </div>
                      </div>
                      {groupFormData.agent_ids.includes(agent.id) && (
                        <CheckCircle className="h-4 w-4 text-cp-indigo" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 p-4 bg-cp-inset border border-cp-line rounded-lg">
              <div className="font-semibold text-sm">Group coordination</div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="use_master"
                  checked={groupFormData.use_master_agent}
                  onChange={(e) => setGroupFormData({...groupFormData, use_master_agent: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="use_master">Assign a lead agent to coordinate the group</Label>
              </div>

              {groupFormData.use_master_agent && (
                <>
                  <div>
                    <Label>Lead agent</Label>
                    <Select 
                      value={groupFormData.master_agent_id || ''} 
                      onValueChange={(v) => setGroupFormData({...groupFormData, master_agent_id: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Choose an agent" /></SelectTrigger>
                      <SelectContent>
                        {customAgents.map(agent => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      This agent will guide the others in the group
                    </p>
                  </div>

                  <div>
                    <Label>How agents run</Label>
                    <Select 
                      value={groupFormData.orchestration_strategy} 
                      onValueChange={(v) => setGroupFormData({...groupFormData, orchestration_strategy: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="master_controlled">Lead agent decides the order</SelectItem>
                        <SelectItem value="sequential">One after another</SelectItem>
                        <SelectItem value="parallel">All at once</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="p-3 bg-cp-inset rounded text-xs">
                    <strong>How it works:</strong> Master agent analyzes the ticket, breaks it into subtasks, 
                    and delegates to appropriate agents in the group. Master synthesizes final output.
                  </div>
                </>
              )}
            </div>

            {!groupFormData.use_master_agent && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="parallel"
                  checked={groupFormData.execute_parallel}
                  onChange={(e) => setGroupFormData({...groupFormData, execute_parallel: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="parallel">Execute agents in parallel</Label>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={createGroup}
              className="flex-1"
              disabled={!canCreate}
              title={permissionDeniedTitle(canCreate, 'create agent groups')}
            >
              Create Group
            </Button>
            <Button variant="outline" onClick={() => setCreateGroupDialogOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
